import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, forkJoin, of, switchMap } from 'rxjs';

import { EntityService } from '../../../core/entity/entity.service';
import { InvitationsResponse } from '../../../core/entity/invitation.types';
import { AuthService } from '../../../core/auth/auth.service';
import { roleLabel, roleTooltip } from '../../../core/auth/role-labels';
import { ENTITY_TYPE_LABELS, EntityDetail, EntityRole } from '../../../core/entity/entity.types';
import { InviteDialogComponent } from '../invite-dialog/invite-dialog';
import { KickstartService } from '../../../core/kickstart/kickstart.service';
import { DfService } from '../../../core/df/df.service';
import { PreApplicationService } from '../../../core/applications/pre-application.service';
import { DIRECT_APPLICATIONS_PAUSED } from '../../../core/applications/direct-applications';
import { PreApplication } from '../../../core/applications/pre-application.types';
import {
  ActivityItem,
  PRE_APP_NOTICES,
  PreAppNotice,
  buildActivity,
  preAppAddDocumentsLink,
  preAppLink,
  preApplicationActivity,
} from './entity-activity';
import { CompanyNameCheckComponent } from '../../../shared/company-name-check/company-name-check';
import { IconComponent } from '../../../shared/icon/icon';
import { StatusChipComponent } from '../../../shared/status-chip/status-chip';
import { ZarPipe } from '../../../shared/pipes/zar.pipe';
import { DashPipe } from '../../../shared/pipes/dash.pipe';
import { AgoPipe } from '../../../shared/pipes/ago.pipe';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '../../../shared/confirm-dialog/confirm-dialog';

/** Roles that may start an application (per the CapX permission table). */
const SUBMIT_ROLES: readonly EntityRole[] = ['entity-admin', 'agent'];

const MONTH_NAMES: Record<number, string> = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December',
};

@Component({
  selector: 'app-entity-dashboard',
  imports: [
    MatCardModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    RouterLink,
    DatePipe,
    CompanyNameCheckComponent,
    IconComponent,
    StatusChipComponent,
    ZarPipe,
    DashPipe,
    AgoPipe,
  ],
  templateUrl: './entity-dashboard.html',
  styleUrl: './entity-dashboard.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntityDashboardComponent implements OnInit {
  private readonly entityService = inject(EntityService);
  private readonly kickstartService = inject(KickstartService);
  private readonly dfService = inject(DfService);
  private readonly preAppService = inject(PreApplicationService);
  private readonly authService = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly user = this.authService.currentUser;
  protected readonly entity = signal<EntityDetail | null>(null);

  // --- Re-election into Debtors Finance (Constitution §A2.3) ---------------
  /**
   * The server evaluates the rule's five conditions; this only renders the
   * answer. Offered on the enquiry row because that is where the client's
   * Debtors Finance interest is already shown — and it is the client's own
   * act: "No Ops action is required to unlock it."
   */
  protected readonly nameToConfirm = signal<string | null>(null);
  protected readonly nameAnsweredNo = signal(false);
  protected readonly dfReElection = computed(() => this.entity()?.df_re_election ?? null);
  protected readonly canReElectDf = computed(() => this.dfReElection()?.available === true);
  protected readonly isReElecting = signal(false);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);
  protected readonly isDeleting = signal(false);

  /** Placeholder signals — no funding data yet; dash pipe renders "—". */
  protected readonly activeFunding = signal<null>(null);
  protected readonly totalFunded = signal<null>(null);

  /** Merged Kickstart + Debtors Finance activity, newest first. */
  protected readonly activity = signal<ActivityItem[]>([]);

  /** The entity's current pre-application (guided intake), or null if none. */
  protected readonly preApplication = signal<PreApplication | null>(null);

  /**
   * The dashboard pre-application banner, or null when there's nothing to show.
   * Only in-flight/attention states surface here — a not-yet-submitted (OPEN) or
   * already-proceeded record shows nothing (the drafts appear in the activity
   * feed). The client never sees the internal recommendation or signals (N4).
   */
  protected readonly preAppNotice = computed<PreAppNotice | null>(() => {
    const pa = this.preApplication();
    if (!pa) {
      return null;
    }
    const copy = PRE_APP_NOTICES[pa.review_status];
    if (!copy) {
      return null;
    }
    return {
      status: pa.review_status,
      ...copy,
      routerLink: preAppLink(this.entityId, pa.review_status, pa.short_id),
      ...(pa.review_status === 'SUBMITTED'
        ? {
            secondaryCta: 'Add documents',
            secondaryLink: preAppAddDocumentsLink(this.entityId, pa.short_id),
          }
        : {}),
    };
  });

  /**
   * The three most recent rows for the activity panel. A submitted (but not yet
   * proceeded) pre-application is folded in here so the panel agrees with the
   * "under review" banner above — the banner is the call to action, this is the
   * record. It's kept out of ``activity`` so ``hasApplied`` and the Applications
   * count stay a pure KS/DF signal.
   */
  protected readonly recentActivity = computed(() => {
    const preApp = preApplicationActivity(this.entityId, this.preApplication());
    const rows = preApp ? [preApp, ...this.activity()] : this.activity();
    return rows
      .slice()
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 3);
  });

  /**
   * Has this business ever applied? Drives the "New application" destination:
   * first-timers get the guidance-led pre-application; returning users go
   * straight to the product cards. ``activity`` holds every KS app + DF facility
   * (not just the recent three), so its length is the full-history signal.
   */
  protected readonly hasApplied = computed(() => this.activity().length > 0);

  /** Paused 2026-10-22 (FLOW-394): direct applications go through the pre-application. */
  protected readonly directApplicationsPaused = DIRECT_APPLICATIONS_PAUSED;

  /** Query params that make the wizard start a new application rather than resume one. */
  protected readonly startNewParams = { start_new: '1' };

  /** Whether the top-right actions menu (hamburger) is open. */
  protected readonly menuOpen = signal(false);

  /** Only an entity-admin may delete a business from its dashboard. */
  protected canDelete(entity: EntityDetail): boolean {
    return entity.your_role === 'entity-admin';
  }

  /** Entity-admins and the linked agent may invite people to the company. */
  protected canInvite(entity: EntityDetail | null): boolean {
    return entity?.your_role === 'entity-admin' || entity?.your_role === 'agent';
  }

  /** Only submit-capable roles see the "New application" CTA. */
  protected canApply(entity: EntityDetail | null): boolean {
    return !!entity && SUBMIT_ROLES.includes(entity.your_role);
  }

  /** True when the signed-in user is viewing this business as its agent. */
  protected isAgent(entity: EntityDetail | null): boolean {
    return entity?.your_role === 'agent';
  }

  /** Set when the agent has just onboarded this client (?onboarded=1). */
  protected readonly justOnboarded = signal(
    this.route.snapshot.queryParamMap.get('onboarded') === '1',
  );

  protected dismissOnboarded(): void {
    this.justOnboarded.set(false);
  }

  protected getTypeLabel(type: string): string {
    return ENTITY_TYPE_LABELS[type] ?? type;
  }

  protected readonly getRoleLabel = roleLabel;

  protected readonly getRoleTooltip = roleTooltip;

  protected getTradingStartLabel(month: number, year: number): string {
    return `${MONTH_NAMES[month] ?? month} ${year}`;
  }

  private readonly entityId = this.route.snapshot.paramMap.get('id') ?? '';

  ngOnInit(): void {
    this.load();
    this.loadNameCheck();
  }

  /** Cosmetic to the page, so its own subscription: a failure shows no question. */
  private loadNameCheck(): void {
    this.entityService
      .getNameCheck(this.entityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ registered_name }) => this.nameToConfirm.set(registered_name),
        error: () => undefined,
      });
  }

  protected answerNameCheck(answer: 'YES' | 'NO'): void {
    this.nameToConfirm.set(null);
    this.nameAnsweredNo.set(answer === 'NO');
    this.entityService
      .answerNameCheck(this.entityId, answer)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ error: () => undefined });
  }

  protected reload(): void {
    this.isLoading.set(true);
    this.loadError.set(false);
    this.cdr.markForCheck();
    this.load();
  }

  private load(): void {
    forkJoin({
      entity: this.entityService.getEntity(this.entityId),
      ksApplications: this.kickstartService.listForEntity(this.entityId),
      dfFacilities: this.dfService.listFacilitiesForEntity(this.entityId),
      // Non-essential: a failure here must not blank the dashboard (forkJoin is
      // all-or-nothing), so degrade to "no banner".
      preApplication: this.preAppService.current(this.entityId).pipe(catchError(() => of(null))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ entity, ksApplications, dfFacilities, preApplication }) => {
          this.entity.set(entity);
          this.activity.set(
            buildActivity(this.entityId, ksApplications.applications, dfFacilities.facilities),
          );
          this.preApplication.set(preApplication);
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading.set(false);
          this.loadError.set(true);
          this.cdr.markForCheck();
        },
      });
  }

  /** Toggle the top-right actions (hamburger) menu. */
  protected toggleMenu(): void {
    this.menuOpen.update((v) => !v);
  }

  protected closeMenu(): void {
    this.menuOpen.set(false);
  }

  /** Close the menu, then navigate to a router-link path. */
  protected goTo(commands: (string | number)[], queryParams?: Record<string, string>): void {
    this.menuOpen.set(false);
    void this.router.navigate(commands, { queryParams });
  }

  protected openInviteDialog(entity: EntityDetail): void {
    this.menuOpen.set(false);
    this.dialog
      .open(InviteDialogComponent, {
        data: { shortId: entity.short_id, entityName: entity.name },
        width: '480px',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res?: InvitationsResponse) => {
        if (res) {
          this.snackBar.open('Invitation sent.', 'Dismiss', { duration: 4000 });
          this.cdr.markForCheck();
        }
      });
  }

  protected deleteEntity(entity: EntityDetail): void {
    this.menuOpen.set(false);
    const data: ConfirmDialogData = {
      title: 'Delete this business?',
      message:
        `“${entity.name}” will be removed from your dashboard. ` +
        'It can be restored later from Deleted businesses.',
      confirmLabel: 'Delete business',
      destructive: true,
    };

    this.dialog
      .open(ConfirmDialogComponent, { data, width: '440px' })
      .afterClosed()
      .pipe(
        switchMap((confirmed) => {
          if (!confirmed) {
            return [];
          }
          this.isDeleting.set(true);
          this.cdr.markForCheck();
          return this.entityService.deleteEntity(entity.short_id);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.snackBar.open(`${entity.name} was deleted.`, 'Dismiss', {
            duration: 5000,
          });
          void this.router.navigate(['/dashboard']);
        },
        error: () => {
          this.isDeleting.set(false);
          this.snackBar.open('Could not delete this business. Please try again.', 'Dismiss', {
            duration: 5000,
          });
          this.cdr.markForCheck();
        },
      });
  }
  /**
   * Record the client's re-election into Debtors Finance (§A2.3). One POST,
   * then re-read the business so the row reflects the new intent. It opens no
   * application — Ops opens products — so the copy says "asked to be
   * considered", never "applied".
   */
  protected reElectDebtorsFinance(): void {
    if (this.isReElecting() || !this.canReElectDf()) return;
    this.isReElecting.set(true);
    this.entityService
      .reElectDebtorsFinance(this.entityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (entity) => {
          this.isReElecting.set(false);
          this.entity.set(entity);
          this.snackBar.open(
            'Debtors Finance added to your enquiry — CapX will come back to you.',
            'Dismiss',
            { duration: 6000 },
          );
        },
        error: (err: unknown) => {
          this.isReElecting.set(false);
          const detail = (err as { error?: { detail?: unknown } })?.error?.detail;
          this.snackBar.open(
            typeof detail === 'string'
              ? detail
              : 'Could not add Debtors Finance right now. Please try again.',
            'Dismiss',
            { duration: 6000 },
          );
        },
      });
  }
}
